/**
 * Start the home dashboard's own requests (layout + disabled widget types) as early as
 * possible — together with the signed-in user's first request, so they travel
 * in the same /batch call — instead of after the page chunk has loaded and the
 * shell has rendered. DashboardPage / DashboardViewer then reuse the promise.
 *
 * Results are reused for a short time only, so later visits still get fresh
 * layouts.
 */
import { DashboardsApi, type DashboardLanding } from '@/services/dashboards.service';
import { WidgetsApi } from '@/services/widgets.service';
import type { UiTheme } from '@/types/tenant.types';

const REUSE_MS = 15_000;
const cache = new Map<string, { at: number; promise: Promise<unknown> }>();

function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < REUSE_MS) return hit.promise as Promise<T>;
  const promise = load();
  cache.set(key, { at: Date.now(), promise });
  promise.catch(() => { if (cache.get(key)?.promise === promise) cache.delete(key); });
  return promise;
}

/** The home layout for the interface theme ('classic' has its own slug). */
export function homeDashboard(theme: UiTheme): Promise<DashboardLanding | null> {
  return once(`home:${theme}`, () =>
    theme === 'classic'
      ? DashboardsApi.getBySlug('home-classic').catch(() => null).then((d) => d ?? DashboardsApi.getLanding())
      : DashboardsApi.getLanding(),
  );
}

/** Forget the reused home layout (after in-place editing, the next read must be fresh — DEF-DB-007). */
export function invalidateHomeDashboard(theme: UiTheme): void {
  cache.delete(`home:${theme}`);
}
/** Widget types switched off in Widget Management. Not GET /widgets/all —
 *  that list is admin-only (UAT DEF-167). */
export function disabledWidgetTypes() {
  return once('widgets:disabled-types', () => WidgetsApi.disabledTypes());
}

const HOME_PATHS = new Set(['/', '/user', '/user/', '/user/dashboard', '/dashboard']);

/** The interface theme cached by BrandingContext (key 'ilearn_ui'), or null when this browser hasn't seen
 *  the tenant's theme yet. Read directly — importing BrandingContext here would make an import cycle. */
function cachedUiTheme(): UiTheme | null {
  try {
    const v = localStorage.getItem('ilearn_ui');
    return v === 'classic' ? 'classic' : v === 'bento' ? 'bento' : null;
  } catch { return null; }
}

/** Called at sign-in restore, before the user is even loaded. Without a known theme nothing is guessed:
 *  prefetching the wrong theme's layout was a wasted request (the page asks once the theme is known). */
export function prefetchHomeIfThere(theme: UiTheme | null = cachedUiTheme()): void {
  if (!HOME_PATHS.has(window.location.pathname)) return;
  if (theme) homeDashboard(theme).catch(() => {});
  disabledWidgetTypes().catch(() => {});
}
